import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Provider } from '../../../types/api.types';
import ProviderCard from '../../../components/ProviderCard';
import Button from '../../../components/common/Button';
import Carousel from '../../../components/common/Carousel';

interface SearchResultsSectionProps {
  providers: Provider[];
  onProviderClick: (provider: Provider) => void;
  onProviderBooking?: (provider: Provider) => void;
  onClearSearch: () => void;
  isSearchApplied: boolean;
}

const ProviderSection: React.FC<SearchResultsSectionProps> = ({
  providers,
  onProviderClick,
  onProviderBooking,
  onClearSearch,
  isSearchApplied,
}) => {
  const navigate = useNavigate();
  const totalResults = providers.length; // Only count providers now
  const hasMoreProviders = providers.length > 3;

  const handleViewAll = () => {
    navigate('/providers');
  };

  return (
    <section id="search-providers" className="py-8 sm:py-16 px-3 xs:px-4 sm:px-6 lg:px-8 bg-white border-b">
      <div className="max-w-7xl mx-auto">

        {isSearchApplied && (
          <div className="flex items-center justify-end mb-4 sm:mb-8">
            <Button
              onClick={onClearSearch}
              variant="secondary"
              size="md"
            >
              Clear Search
            </Button>
          </div>
        )}


        {totalResults === 0 && (
          <div className="text-center py-8 sm:py-12">
            <p className="text-lg sm:text-xl text-gray-600 mb-3 sm:mb-4">No providers found</p>
            <p className="text-sm sm:text-base text-gray-500">Try adjusting your search criteria</p>
          </div>
        )}

        {providers?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Providers</h2>
              {hasMoreProviders && (
                <Button
                  onClick={handleViewAll}
                  variant="outline"
                  className="flex items-center space-x-1 text-xs sm:text-sm px-2.5 py-1 sm:px-3 sm:py-1.5 h-auto"
                >
                  <span>View All ({providers?.length})</span>
                  <ArrowRightIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </Button>
              )}
            </div>
            <Carousel
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 3 }}
              gap={24}
              autoPlay={providers.length > 3}
              autoPlayInterval={4000}
            >
              {providers.map((provider) => (
                <ProviderCard
                  key={provider._id}
                  provider={provider}
                  onClick={() => onProviderClick(provider)}
                  onBookClick={onProviderBooking}
                />
              ))}
            </Carousel>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProviderSection;
